import { QueryClient, QueryKey } from "@tanstack/react-query";

import { applyUpdatedWithValueDelta } from "lib/utils";
import { BidQuery, BidsQuery } from "../../../../../generated/app-service-gql/graphql";
import { BidEntityType as EntityType } from "../../../../../types/graphQLEnums";
import { BidDetails } from "../useBid";
import { BidData } from "../useBids";
import { updateBidEntityQuery } from "./updateBidEntityQuery";

// The entity types the bid list query shows on each bid
const BID_LIST_ENTITY_TYPES = [
  EntityType.Buyer,
  EntityType.Supplier,
  EntityType.Organisation,
  EntityType.CustomOrganisation,
  EntityType.Framework,
];

type BidEntityResponse = {
  entityId: string;
  entityType: EntityType;
  bidId: string;
  bidName: string;
};

/**
 * Function to help update the cached entities when adding/removing entities to/from a bid
 */
export async function updateBidEntityCache({
  queryClient,
  entities,
  isAdding,
}: {
  queryClient: QueryClient;
  entities: BidEntityResponse[];
  isAdding: boolean;
}) {
  const allBidsQueries = queryClient.getQueryCache().findAll({ queryKey: ["bids"] });

  for (const entity of entities) {
    const { entityId, entityType, bidId, bidName } = entity;

    // Updates the relevant query caches of whichever entity type is being updated
    // e.g notice, contact, document, framework etc
    updateBidEntityQuery({
      queryClient,
      entityId,
      entityType,
      bidName,
      bidId,
      isAdding,
    });

    // TODO: Implement when we add the bidsSavedToEntity queries
    // Update saved to entity cache for this entity ID
    // updateCachedBidsSavedToEntity({
    //   queryClient,
    //   entityId,
    //   bidId,
    //   isAdding,
    // });

    // ADDING ENTITY LOGIC
    if (isAdding) {
      // Since backend does not return full updated entity info,
      // the safest is to invalidate the specific bid cache.
      await queryClient.invalidateQueries({ queryKey: ["bid", bidId] });

      // We don't currently need to update any other entity types when adding entities,
      // because only buyers and supplier entities are currently on the bid list query results.
      if (!BID_LIST_ENTITY_TYPES.includes(entityType)) {
        continue;
      }

      // Add buyer/supplier/framework to bid list queries
      for (const { queryKey } of allBidsQueries) {
        const data = queryClient.getQueryData<BidsQuery>(queryKey);
        if (!data?.bids) continue;
        const queryIncludesBid = data.bids.results.some((l) => l.id === bidId);

        // If query includes bid that had an entity removed from it, invalidate the bid list query cache to get updated buyers/suppliers/frameworks
        if (queryIncludesBid) {
          await queryClient.invalidateQueries({ queryKey });
        }
      }

      // Add bid to entity it was saved to
      // REMOVING ENTITY LOGIC
    } else {
      const entityRemover = bidDetailEntityRemover(entityId)[entityType];
      if (!entityRemover) {
        throw new Error(`Unhandled bid entity type: ${entityType}`);
      }

      // Update the bid detail page cache
      updateBidQuery(queryClient, bidId, entityRemover);

      // Only need to update bid list query caches if the entity type is a buyer, supplier, custom buyer or framework
      if (!BID_LIST_ENTITY_TYPES.includes(entityType)) {
        continue;
      }

      for (const { queryKey } of allBidsQueries) {
        const data = queryClient.getQueryData<BidsQuery>(queryKey);
        if (!data?.bids) continue;

        const queryIncludesBid = data.bids.results.some((l) => l.id === bidId);
        // If query includes bid that had an entity removed from it, update the bid to remove the buyer/supplier/framework/custom buyer
        if (queryIncludesBid) {
          updateSearchBidsQuery(queryClient, queryKey, [bidId], (bid) => ({
            ...bid,
            buyers: bid.buyers.filter((b) => b.id !== entityId),
            suppliers: bid.suppliers.filter((s) => s.id !== entityId),
            frameworks: bid.framework?.id !== entityId ? bid.framework : null,
            customBuyers: bid.customBuyers.filter((b) => b.id !== entityId),
          }));
        }
      }
    }
  }
}

/**
 * Contains all of the cache update actions to remove each type of entity from a bid detail object
 * @param entityId
 * @returns
 */
function bidDetailEntityRemover(
  entityId: string,
): Partial<Record<EntityType, (bid: BidDetails) => BidDetails>> {
  return {
    [EntityType.Notice]: (bid) => ({
      ...bid,
      notice: null,
    }),
    [EntityType.Buyer]: (bid) => ({
      ...bid,
      buyers: bid.buyers.filter((b) => b.id !== entityId),
    }),
    [EntityType.Supplier]: (bid) => ({
      ...bid,
      suppliers: bid.suppliers.filter((s) => s.id !== entityId),
    }),
    [EntityType.Organisation]: (bid) => ({
      ...bid,
      buyers: bid.buyers.filter((b) => b.id !== entityId),
      suppliers: bid.suppliers.filter((s) => s.id !== entityId),
    }),
    [EntityType.CustomOrganisation]: (bid) => ({
      ...bid,
      customBuyers: bid.customBuyers.filter((b) => b.id !== entityId),
    }),
    [EntityType.Framework]: (bid) => ({
      ...bid,
      framework: null,
    }),
  };
}

/**
 * Updates the bid query cache for a given bid ID
 * @param queryClient
 * @param bidId
 * @param updater
 */
export function updateBidQuery(
  queryClient: QueryClient,
  bidId: string,
  updater: (bid: BidQuery["bid"]) => BidQuery["bid"],
) {
  const key = ["bid", bidId];
  queryClient.setQueryData<BidQuery>(key, (prev) => {
    if (!prev) return prev;
    return { bid: updater(prev.bid) };
  });
}

/**
 * Updates the useBids query cache for given bid IDs
 * @param queryClient
 * @param queryKey
 * @param bidIds
 * @param updater
 */
export function updateSearchBidsQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  bidIds: string[],
  updater: (bid: BidData) => BidData,
  valueDeltaOverride?: number,
) {
  queryClient.setQueryData<BidsQuery>(queryKey, (prev) => {
    if (!prev) return prev;
    const { results, valueDelta } = applyUpdatedWithValueDelta(prev.bids.results, bidIds, updater);

    return {
      bids: {
        ...prev.bids,
        results,
        valueSum: (prev.bids.valueSum ?? 0) + (valueDeltaOverride ?? valueDelta),
      },
    };
  });
}
